/**
 * F-70 entry point in the F-27 selection toolbar (docs/08 §2 — one
 * `registerToolbarAction` call; the toolbar never special-cases it).
 *
 * Since 2026-09-04 the icon opens a MENU with two choices (TranslateMenu.tsx)
 * rather than the dialog straight away:
 *  - **Translate this cell** — the selected cells, translated in one request,
 *    each answer appended under the cell's own text (cell-translate.ts);
 *  - **Translate whole column…** — the F-70 job: opens the dialog with THAT
 *    column as the target (a non-source column), or with a target PICKER (the
 *    Original Script column itself, 2026-09-03 — the column people click when
 *    they want it translated).
 *
 * So the icon now shows for ANY cells selection that could do either: a
 * single translatable column, the source column, or any cell with prose in
 * it. The menu greys the row that does not apply and says why.
 *
 * The column rule and its reasons live in `eligibility.ts` (pure, tested);
 * the cell rule in `cell-translate.ts` (pure, tested); this module only wires
 * them into the registry.
 */
import { useStore } from "zustand";

import {
  registerToolbarAction,
  type ToolbarActionContext,
} from "../../registries/toolbar-actions/index.js";
import { iconTranslate } from "../../ui/icons.js";
import { isJobFor, isJobRunning } from "./job-view.js";
import { translateUi } from "./store.js";
import {
  cellsTranslatable,
  columnTranslatable,
  isSourceSelection,
  TranslateMenu,
} from "./TranslateMenu.js";
import { useTranslationJob } from "./use-job.js";

/**
 * The pill button's icon: a spinner while THIS column is being translated by
 * the job, or while this browser's cell request is out — so the toolbar
 * shows a run is in flight rather than inviting a second one.
 *
 * The job state is read from the DOC (F-70), not from local state — so the
 * spinner shows for a run ANOTHER user started, and for one still going after
 * a reload. The cell request is this browser's alone (store.ts `cellRun`).
 */
function TranslateIcon({ ctx }: { ctx: ToolbarActionContext }) {
  const job = useTranslationJob(ctx.session);
  const cellRun = useStore(translateUi, (s) => s.cellRun);
  const sel = ctx.selection;
  if (sel.kind !== "cells") return iconTranslate;
  const colId = sel.colIds[0];
  const columnBusy =
    colId !== undefined && isJobRunning(job) && isJobFor(job, sel.sheetId, colId);
  const cellsBusy = cellRun !== null && cellRun.boardId === ctx.session.boardId;
  return columnBusy || cellsBusy ? (
    <span className="st-spinner" aria-hidden="true" />
  ) : (
    iconTranslate
  );
}

registerToolbarAction({
  id: "translate-column",
  appliesTo: "cells",
  tooltip: (ctx) =>
    isSourceSelection(ctx)
      ? "Translate — this cell, or Original Script into a column"
      : "Translate — this cell, or the whole column",
  order: 20,
  // HIDDEN, not disabled, when the selection could do neither — see the
  // eligibility module note on why an inert translate icon is worse than none.
  visible: (ctx) =>
    ctx.selection.kind === "cells" && (cellsTranslatable(ctx) || columnTranslatable(ctx)),
  icon: (ctx) => <TranslateIcon ctx={ctx} />,
  popover: (ctx) => <TranslateMenu ctx={ctx} />,
});
